
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { useCollectionRealtime } from '../realtime/useCollectionRealtime';

export const useUserVotes = () => {
  const [userVotes, setUserVotes] = useState<Record<string, number>>({});
  const [isLoadingVotes, setIsLoadingVotes] = useState(false);
  const fetchInProgress = useRef(false);
  
  const { user } = useAuth();
  
  const fetchUserVotes = useCallback(async () => {
    // For unauthenticated users, just return empty votes
    if (!user) {
      setUserVotes({});
      return;
    }
    
    // Prevent multiple concurrent fetches
    if (fetchInProgress.current) {
      return;
    }
    
    try {
      fetchInProgress.current = true;
      setIsLoadingVotes(true);
      console.log('Fetching user votes for user:', user.id);
      
      const { data: votesData, error: votesError } = await supabase
        .from('poll_votes')
        .select('poll_id, option_id')
        .eq('user_id', user.id);
      
      if (votesError) {
        console.error('Error fetching user votes:', votesError);
      } else if (votesData) {
        console.log('Retrieved votes data:', votesData);
        const votes: Record<string, number> = {};
        votesData.forEach((vote) => {
          votes[vote.poll_id] = vote.option_id;
        });
        console.log('Setting user votes state:', votes);
        setUserVotes(votes);
      }
    } catch (error) {
      console.error('Error fetching user votes:', error);
    } finally {
      setIsLoadingVotes(false);
      fetchInProgress.current = false;
    }
  }, [user]);
  
  // Refresh votes when user changes
  useEffect(() => {
    if (user) {
      console.log('User changed, fetching votes');
      fetchUserVotes();
    } else {
      setUserVotes({});
    }
  }, [user, fetchUserVotes]);
  
  // Use Universal Real-time Manager for poll votes subscriptions
  useCollectionRealtime(
    'votes',
    { userId: user?.id }, // Filter by user ID
    {
      onItemAdded: useCallback((payload: any) => {
        if (!user) return;
        
        console.log('Universal Realtime: New vote received:', payload);
        
        const vote = payload;
        if (vote.user_id === user.id && vote.poll_id && vote.option_id) {
          console.log(`Universal Realtime: User ${user.id} voted for poll ${vote.poll_id}, option ${vote.option_id}`);
          setUserVotes(prevVotes => ({
            ...prevVotes,
            [vote.poll_id]: vote.option_id
          }));
        }
      }, [user]),
      
      onItemUpdated: useCallback((payload: any) => {
        if (!user) return;
        
        console.log('Universal Realtime: Vote updated:', payload);
        
        const vote = payload;
        if (vote.user_id === user.id && vote.poll_id && vote.option_id) {
          console.log(`Universal Realtime: User ${user.id} vote updated for poll ${vote.poll_id}, option ${vote.option_id}`);
          setUserVotes(prevVotes => ({
            ...prevVotes,
            [vote.poll_id]: vote.option_id
          }));
        }
      }, [user]),
      
      onItemRemoved: useCallback((itemId: string) => {
        if (!user) return;
        
        console.log('Universal Realtime: Vote removed:', itemId);
        
        // For deletions, we need to refetch to determine which poll was affected
        // This is a limitation but maintains consistency
        fetchUserVotes();
      }, [user, fetchUserVotes]),
      
      onError: useCallback((error: any) => {
        console.error('Universal Realtime: Poll votes error:', error);
      }, [])
    },
    !!user // Only enabled when user is authenticated
  );
  
  // Provide a method to refresh votes manually
  const refreshVotes = useCallback(() => {
    if (user) {
      fetchUserVotes();
    }
  }, [user, fetchUserVotes]);
  
  // Provide a method to manually update the votes (for optimistic updates)
  const updateVote = useCallback((pollId: string, optionId: number) => {
    console.log(`Manual vote update: poll ${pollId}, option ${optionId}`);
    setUserVotes(prevVotes => ({
      ...prevVotes,
      [pollId]: optionId
    }));
  }, []);
  
  return {
    userVotes,
    setUserVotes,
    isLoadingVotes,
    refreshVotes,
    updateVote
  };
};
